// VerifyEmailPage — landing for BE-23 verification confirmation links.
//
// User clicks the link in their email (`/verify-email?token=...`).
// On mount we POST /api/v1/auth/verify/confirm. On success, redirect
// to /login after a short delay so the user can read the confirmation.
// On failure: show specific error (expired/used/unknown) with a
// resend-link path.

import { Button, buttonVariants, Card, IntelliAuthLogo } from '@intelliauth/ui'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { CpV2ApiError, cpClient } from '@/lib/api/cpClient'

type Status = 'verifying' | 'success' | 'error'

export default function VerifyEmailPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') ?? ''

  const [status, setStatus] = useState<Status>('verifying')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setErrorMsg('Verification link is missing the token. Check the URL in your email.')
      return
    }

    let cancelled = false
    cpClient()
      .post('/api/v1/auth/verify/confirm', { body: { token } })
      .then(() => {
        if (cancelled) return
        setStatus('success')
        // Auto-redirect after 2s so the user sees the confirmation.
        setTimeout(() => {
          if (!cancelled) navigate('/login')
        }, 2000)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setStatus('error')
        if (err instanceof CpV2ApiError) {
          if (err.status === 410) {
            setErrorMsg(
              'This verification link has expired. If you already verified your email, just sign in — otherwise request a fresh link below.',
            )
          } else if (err.status === 404) {
            setErrorMsg('Verification token not found. Check the link in your email.')
          } else {
            setErrorMsg(err.message)
          }
        } else {
          setErrorMsg(err instanceof Error ? err.message : 'Verification failed.')
        }
      })

    return () => {
      cancelled = true
    }
  }, [token, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-[440px]">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center">
            <IntelliAuthLogo size="md" />
          </Link>
        </div>

        <Card className="p-8 text-center">
          {status === 'verifying' && (
            <>
              <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-5">
                <Loader2 className="h-8 w-8 text-primary animate-spin" />
              </div>
              <h1 className="text-2xl font-semibold text-foreground mb-2">Verifying...</h1>
              <p className="text-sm text-muted-foreground">
                Confirming your email with the server.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mb-5">
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
              </div>
              <h1 className="text-2xl font-semibold text-foreground mb-2">Email verified!</h1>
              <p className="text-sm text-muted-foreground mb-6">
                Your account is now active. Redirecting you to sign in...
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate('/login')}
                className="w-full"
              >
                Continue to sign in
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-5">
                <AlertCircle className="h-8 w-8 text-red-500" />
              </div>
              <h1 className="text-2xl font-semibold text-foreground mb-2">Verification failed</h1>
              <p className="text-sm text-muted-foreground mb-6">{errorMsg}</p>
              <div className="space-y-2">
                <Link
                  to="/login"
                  className={`${buttonVariants({ variant: 'primary', size: 'sm' })} w-full`}
                >
                  Go to sign in
                </Link>
                <Link
                  to="/verify-email/sent"
                  className={`${buttonVariants({ variant: 'outline', size: 'sm' })} w-full`}
                >
                  Request a new link
                </Link>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  )
}
